<template>
  <Loader :value="promises.fetch" class="integrations-mutate">
    <template #default>
      <div class="integrations-mutate__categories">
        <div
          v-for="category in categories"
          :key="category.key"
          class="integrations-mutate__category"
          :class="{ 'integrations-mutate__category--faded': highlighted && category.key !== highlighted }"
          :data-category="category.key || 'other'"
        >
          <label v-if="categories.length > 1">{{ category.label }}</label>
          <div class="integrations-mutate__items">
            <Card
              v-for="entry in items.filter(i => i.source.category === category.key)"
              :key="entry.id"
              :value="entry"
              :promise="item && entry.id === item.id && !item.source.schema.length ? promises.update : undefined"
              @update="item = entry"
              @uninstalled="() => uninstalled(entry)"
              @multiinstall="() => preinstall(allSources.find(a => a.source.id === entry.source.id))"
            />
            <Card
              v-for="entry in availableSources.filter(i => i.source.category === category.key)"
              :key="entry.source.id"
              :value="entry"
              :promise="
                item && entry.source.id === item.source.id && !item.source.schema.length ? promises.update : undefined
              "
              @install="() => preinstall(entry)"
            />
          </div>
        </div>
      </div>
      <Modal :show="Boolean(item && item.source.schema.length)" @close="item = null">
        <Settings
          v-if="item"
          :value="item.data"
          :status="item.status"
          :gdpr="item.source.gdpr"
          :promise="promises.update"
          :schema="item.source.schema"
          :source="item.source"
          @confirm="update"
          @cancel="item = null"
        />
      </Modal>
    </template>
  </Loader>
</template>

<script>
import { i18n } from '@/i18n'
import Store from '@/store'
import API from '@/api/Integration'
import { SortByKey } from '@/utils'

import { ModalComponent as Modal } from 'vue-elder-modal'
import Card from '@/components/Integration/Card'
import Settings from '@/components/Integration/Settings'

const meta = {
  skip: !Store.getters.isAdmin,
  id: 'integrations',
  label: i18n.tc('integration', 2),
  icon: ['fal', 'puzzle-piece'],
}

export { meta }
export default {
  props: {
    isActive: Boolean,
    reference: Object,
  },
  watch: {
    isActive: {
      handler: 'fetch',
      immediate: true,
    },
  },
  data() {
    return {
      promises: {
        fetch: null,
        update: null,
      },
      highlighted: null,
      sources: [],
      items: [],
      item: null,
    }
  },
  computed: {
    categories() {
      return [
        ...[...new Set(this.allSources.map(({ source }) => source.category).filter(Boolean))]
          .map(key => ({ label: this.$t(`integration-category:${key}`), key }))
          .sort(SortByKey('key')),
        { label: this.$t(`integration-category:other`), key: null },
      ]
    },
    allSources() {
      return this.sources.map(source => ({ source }))
    },
    availableSources() {
      return this.sources
        .filter(s => !this.items.some(i => i.source.id === s.id))
        .map(source => ({
          source,
        }))
    },
  },
  methods: {
    fetch() {
      if (!this.isActive) return
      this.promises.fetch = API.get(
        {
          filter: { reference: this.reference ? this.reference.ref : null },
          placement: this.reference ? this.reference.onModel.toLowerCase() : 'global',
        },
        { query: 'Integrations' },
      ).then(({ Integrations, IntegrationSources }) => {
        this.sources = IntegrationSources
        this.items = Integrations

        if (this.$route.query.category) {
          setTimeout(() => {
            let el = this.$el.querySelector(`[data-category="${this.$route.query.category}"]`)
            if (!el) return

            window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 100, behavior: 'smooth' })
            this.highlighted = this.$route.query.category
            setTimeout(() => (this.highlighted = null), 2000)
          }, 150)
        }
      })
    },
    update(data) {
      if (!this.item) return

      this.item.data = data

      if (!this.item.id) return this.install(this.item)

      this.promises.update = API.mutate({
        id: this.item.id,
        data,
      }).then(data => {
        let index = this.items.findIndex(i => i.id === this.item.id)
        this.items.splice(index, 1, data)
        this.item = null

        if ('string' == typeof data.installer.url && data.installer.url.startsWith('http'))
          return (window.location.href = data.installer.url)
        return this.$router.push(data.installer.url)
      })
    },
    preinstall(item) {
      this.item = { source: item.source, data: item.source.defaultData || {} }
      if (!item.source.schema.length) this.install(this.item)
    },
    install(item) {
      const { data, source } = item

      this.promises.update = API.mutate({
        data,
        source: source.id,
        reference: this.reference,
      })
        .then(data => {
          this.items.push(data)
          this.item = null
          if (!data.installer.url) return

          if ('string' == typeof data.installer.url && data.installer.url.startsWith('http'))
            return (window.location.href = data.installer.url)
          return this.$router.push(data.installer.url)
        })
        .then(() => this.$emit('installed'))
    },
    uninstalled(item) {
      this.items = this.items.filter(i => i.id !== item.id)
    },
  },
  components: {
    Card,
    Settings,
    Modal,
  },
}
</script>

<style lang="scss">
.integrations-mutate {
  .elder-loader__element {
    background-color: transparent;
  }

  .elder-modal {
    z-index: 1010 !important;
  }

  &__categories {
    display: flex;
    flex-wrap: wrap;
    gap: 3rem;
  }

  &__category {
    flex-grow: 1;
    transition: opacity 600ms ease-out;
    width: 100%;

    &--faded {
      opacity: 0.1;
    }

    label {
      font-weight: bold;
      display: block;
      margin-bottom: 2rem;
      padding-bottom: 0.5rem;
      font-size: 1.2em;
      border-bottom: 2px solid rgba(black, 0.03);
    }
  }

  &__items {
    display: grid;
    gap: 2rem;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  }

  &__navigation {
    margin-top: 2rem;
    display: flex;
    justify-content: center;
    gap: 1rem;
  }
}
</style>
